/** The org's postal address + main phone — view and edit.
 *
 * Backlog 2026-09-22 — the sibling gap to the contact-people card: the
 * settings page showed no address at all. The columns have existed since
 * the partner-console org form, and the invoice PDF prints them, but
 * only the partner console could edit them.
 *
 * PUT replaces the address wholesale (it is one fact, not a set of
 * independent ones), so this card edits a local draft and saves in one
 * shot. The country is shared with the org profile — the backend keeps
 * the two in step so the page can't show two different countries.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CountrySelector } from "@/components/CountrySelector";
import PhoneInput from "@/components/PhoneInput";
import { ApiPaths, api } from "@/lib/api";
import { describeApiError, isMfaRequired } from "@/lib/apiErrors";

export type OrgAddress = {
  line1: string;
  line2: string;
  city: string;
  postal_code: string;
  state: string;
  country: string;
};

type Payload = { address: OrgAddress; phone: string };

const EMPTY: OrgAddress = {
  line1: "",
  line2: "",
  city: "",
  postal_code: "",
  state: "",
  country: "",
};

export function OrgAddressCard() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [address, setAddress] = useState<OrgAddress>(EMPTY);
  const [phone, setPhone] = useState("");
  const [saved, setSaved] = useState(false);

  const current = useQuery({
    queryKey: ["org-address"],
    queryFn: async () => (await api.get<Payload>(ApiPaths.orgAddress)).data,
  });

  useEffect(() => {
    if (current.data) {
      setAddress({ ...EMPTY, ...current.data.address });
      setPhone(current.data.phone ?? "");
    }
  }, [current.data]);

  const save = useMutation({
    mutationFn: async () => {
      const res = await api.put<Payload>(ApiPaths.orgAddress, {
        address: { ...address, country: address.country.trim().toUpperCase() },
        phone: phone.trim(),
      });
      return res.data;
    },
    onSuccess: (next) => {
      qc.setQueryData(["org-address"], next);
      // The org country rides along with the address country, so the
      // profile card must not keep showing the stale one.
      void qc.invalidateQueries({ queryKey: ["org-me"] });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  const patch = (field: keyof OrgAddress, value: string) =>
    setAddress((prev) => ({ ...prev, [field]: value }));

  const field = (
    key: keyof OrgAddress,
    labelKey: string,
    fallback: string,
    maxLength?: number,
  ) => (
    <label className="space-y-1 text-xs text-nb-text-2">
      <span>{t(labelKey, fallback)}</span>
      <input
        className="input"
        value={address[key]}
        maxLength={maxLength}
        onChange={(e) => patch(key, e.target.value)}
        aria-label={t(labelKey, fallback)}
      />
    </label>
  );

  return (
    <section className="card space-y-3">
      <header>
        <h2 className="text-sm font-semibold uppercase tracking-wide text-nb-text-2">
          {t("admin.orgAddress.cardTitle", "Address & phone")}
        </h2>
        <p className="text-xs text-nb-text-3">
          {t(
            "admin.orgAddress.cardHint",
            "Your organization's postal address and main phone number. The address is what appears on your invoices.",
          )}
        </p>
      </header>

      {current.isLoading && (
        <p className="text-sm text-nb-text-3">{t("common.loading")}</p>
      )}

      <div className="grid gap-2 sm:grid-cols-2">
        <div className="sm:col-span-2">
          {field("line1", "admin.orgAddress.line1Label", "Street address")}
        </div>
        <div className="sm:col-span-2">
          {field("line2", "admin.orgAddress.line2Label", "Address line 2 (optional)")}
        </div>
        {field("postal_code", "admin.orgAddress.postalCodeLabel", "Postcode")}
        {field("city", "admin.orgAddress.cityLabel", "City")}
        {field("state", "admin.orgAddress.stateLabel", "Region / state (optional)")}
        {/* Uniformity 2026-09-23 — this was a 2-character text box while
            every other country field in the app uses the shared
            searchable picker. Asking an operator to know that Belgium
            is "BE" is not a reasonable thing to ask. */}
        <label className="space-y-1 text-xs text-nb-text-2">
          <span>{t("admin.orgAddress.countryLabel", "Country")}</span>
          <CountrySelector
            value={address.country}
            onChange={(code) => patch("country", code)}
            allowEmpty
            aria-label={t("admin.orgAddress.countryLabel", "Country")}
          />
        </label>
        <label className="space-y-1 text-xs text-nb-text-2 sm:col-span-2">
          <span>{t("admin.orgAddress.phoneLabel", "Main phone")}</span>
          <PhoneInput
            value={phone}
            onChange={setPhone}
            aria-label={t("admin.orgAddress.phoneLabel", "Main phone")}
          />
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          className="btn-primary text-sm"
          onClick={() => save.mutate()}
          disabled={save.isPending}
        >
          {save.isPending
            ? t("common.saving")
            : t("admin.orgAddress.saveButton", "Save address")}
        </button>
        {saved && (
          <span className="text-xs text-cvp-ok">
            {t("admin.orgAddress.savedAck", "Saved")}
          </span>
        )}
      </div>

      {save.isError && (
        <p className="text-xs text-cvp-danger">
          {isMfaRequired(save.error)
            ? t("admin.mfaRequiredHint")
            : describeApiError(
                save.error,
                t("admin.orgAddress.saveErrorGeneric", "Could not save the address."),
              )}
        </p>
      )}
    </section>
  );
}
